import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Typography, Box, Modal, Snackbar, makeStyles, Button, Grid, TextField, FormControlLabel, Checkbox, Divider } from '@material-ui/core';
import { Stack } from '@mui/material';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import { api_call } from '../helpers/untils';
import ModalVideo from 'react-modal-video';
import 'react-modal-video/scss/modal-video.scss';
import { SHRED_GEOFENCE_VIDEO_ID, SHRED_TURO_VIDEO_ID } from '../config/constant';
import GeofenceAutoCompleteDropdown from '../components/GeofenceAutoCompleteDropdown';

// One geofence section (Pickup or Return) holds its own geofence selection, a
// deadline (in minutes) and the Kill / Horn / Lock command flags.
export const emptyGeofenceSection = () => ({ geofenceIds: [], deadline: '', kill: false, horn: false, lock: false })
export const defaultGeofenceSettings = () => ({
  pickup: { ...emptyGeofenceSection(), deadline: 120 },
  return: { ...emptyGeofenceSection(), deadline: 30 },
})

// Merge a saved (possibly partial / legacy) settings object onto the defaults so
// every section always has all fields.
export const mergeGeofenceSettings = (saved) => {
  const defaults = defaultGeofenceSettings()
  return {
    pickup: { ...defaults.pickup, ...(saved?.pickup || {}) },
    return: { ...defaults.return, ...(saved?.return || {}) },
  }
}

// Flat union of pickup + return geofence ids, kept for the legacy
// selectedGeofenceIds consumers (geofence notify / apply-to-all-cars).
export const unionGeofenceIds = (gs) => {
  const pickupIds = gs?.pickup?.geofenceIds || []
  const returnIds = gs?.return?.geofenceIds || []
  return [...new Set([...pickupIds, ...returnIds])]
}

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '660px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
  maxHeight: '100vh',
  overflowY: 'auto'
};
const useStyles = makeStyles(() => ({
  sectionBox: {
    marginBottom: '32px',
  },
}));

const GeofenceSectionForm = (props) => {
  const { title, section, sectionData, geofenceList, updateSection } = props
  return (
    <Box>
      <Stack direction={`row`} sx={{ justifyContent: 'space-between', alignItems: 'center' }} spacing={2}>
        <Typography variant="h6" component="h2">{title}</Typography>
        <Stack direction={`row`} sx={{ justifyContent: 'flex-end', alignItems: 'center' }} spacing={1}>
          <Typography variant='p'>Deadline</Typography>
          <TextField
            variant="filled"
            label=""
            value={sectionData.deadline}
            onChange={(event) => updateSection(section, 'deadline', event.target.value)}
            style={{ width: '90px' }}
            inputProps={{ type: 'number', min: 0, placeholder: ' ' }}
          />
          <Typography variant='p'>Minutes</Typography>
        </Stack>
      </Stack>

      <Grid container spacing={2} alignItems="center" sx={{ marginTop: '4px' }}>
        <Grid item xs={12} sm={6}>
          <GeofenceAutoCompleteDropdown
            label={`Select Geofences`}
            definitions={geofenceList}
            value={sectionData.geofenceIds}
            setValue={(ids) => updateSection(section, 'geofenceIds', ids)}
            multiple={true}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack direction={`row`} sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
            <FormControlLabel
              control={<Checkbox checked={!!sectionData.kill} onChange={(event) => updateSection(section, 'kill', event.target.checked)} />}
              label="Kill"
              labelPlacement="end"
            />
            <FormControlLabel
              control={<Checkbox checked={!!sectionData.horn} onChange={(event) => updateSection(section, 'horn', event.target.checked)} />}
              label="Horn"
              labelPlacement="end"
            />
            <FormControlLabel
              control={<Checkbox checked={!!sectionData.lock} onChange={(event) => updateSection(section, 'lock', event.target.checked)} />}
              label="Lock"
              labelPlacement="end"
            />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}

const LinkGeofenceModal = (props) => {
  const { open, setOpen, selectedDeviceId, geofenceSettings, setGeofenceSettings, showToast } = props;

  const user = useSelector((state) => state.session.user);

  const classes = useStyles();

  const [apiLoading, setApiLoading] = useState(false)
  const [tmpSettings, setTmpSettings] = useState(defaultGeofenceSettings())
  const [isVideoOpen, setIsVideoOpen] = useState(false)

  const handleModalClose = () => {
    setOpen("")
  }

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  //////////////////////// app modal close feature //////////////////////////////////////////
  const appModalCloseTimestamp = useSelector((state) => state.layout.appModalCloseTimestamp);
  useEffect(() => {
    if (appModalCloseTimestamp) {
      setOpen(false)
    }
  }, [appModalCloseTimestamp]);
  ///////////////////////////////////////////////////////////////////////////////////////////

  useEffect(() => {
    if (open) {
      loadGeofences()
    }
  }, [open])

  useEffect(() => {
    if (open) {
      setTmpSettings(mergeGeofenceSettings(geofenceSettings))
    }
  }, [open, geofenceSettings])

  const [geofenceList, setGeofenceList] = useState([])
  const loadGeofences = async () => {
    let url = '/api/geofences/get-list'
    const response = await api_call(url);
    if (response.ok) {
      const geofence_list = await response.json()
      setGeofenceList(geofence_list)
    }
  }

  const updateSection = (section, field, value) => {
    setTmpSettings((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }))
  }

  const onClickSaveSetting = async () => {
    setGeofenceSettings(tmpSettings)
    // Turn on Late Pickup / Late Return email notifications for this device and set
    // their "Send Email to" address to the current user's profile email (done backend-side).
    if (selectedDeviceId) {
      setApiLoading(true)
      await api_call('/api/devices/enable-late-geofence-notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: selectedDeviceId }),
      })
      setApiLoading(false)
    }
    handleModalClose()
  }

  const onClickApplyAllCars = async () => {
    if (user.administrator) {
      return false;
    }
    setApiLoading(true);
    const url = `/api/links/apply-geofences-to-all-cars`;
    const payload = {
      deviceId: selectedDeviceId,
      geofenceSettings: tmpSettings,
      selectedGeofenceIds: geofenceSettings.return.geofenceIds ?? [] // unionGeofenceIds(tmpSettings)
    };
    let response = await api_call(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    setApiLoading(false);
    if (response.ok) {
      showToast('Applied to all cars successfully!');
    } else {
      showToast('Invalid request!');
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={handleModalClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box sx={modalStyle}>
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={snackbarOpen}
            onClose={() => { setSnackbarOpen(false) }}
            autoHideDuration={1500}
            message={toastMessage}
          />
          <Stack direction={`row`} sx={{ justifyContent: 'space-between', alignItems: 'center' }} spacing={2}>
            <div></div>
            <Typography variant='p' onClick={() => setIsVideoOpen(true)} style={{ cursor: 'pointer', color: '#1366ff' }}>Video</Typography>
          </Stack>

          <Box sx={{ paddingTop: 5 }}>
            <Box className={classes.sectionBox}>
              <GeofenceSectionForm
                title={`Pickup`}
                section={`pickup`}
                sectionData={tmpSettings.pickup}
                geofenceList={geofenceList}
                updateSection={updateSection}
              />
            </Box>

            <Divider />

            <Box className={classes.sectionBox} sx={{ marginTop: '32px' }}>
              <GeofenceSectionForm
                title={`Return`}
                section={`return`}
                sectionData={tmpSettings.return}
                geofenceList={geofenceList}
                updateSection={updateSection}
              />
            </Box>

            <Box sx={{ marginTop: '24px' }}>
              <Grid container justifyContent='center' alignItems='center' spacing={2}>
                <Grid item>
                  <Stack direction={`row`} spacing={1} sx={{ justifyContent: 'center', alignItems: 'center' }}>
                    <Button
                      type="button"
                      color="primary"
                      variant="contained"
                      size="large"
                      disabled={apiLoading}
                      onClick={() => onClickSaveSetting()}
                    >
                      Ok
                    </Button>
                    <Button
                      type="button"
                      color="primary"
                      variant="outlined"
                      size="large"
                      onClick={() => handleModalClose()}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
                {
                  (!user.administrator) ? (
                    <Grid item>
                      <Button
                        type="button"
                        color="secondary"
                        variant="contained"
                        size="large"
                        disabled={apiLoading}
                        onClick={() => onClickApplyAllCars()}
                      >
                        Apply to All Cars
                      </Button>
                    </Grid>
                  ) : (
                    <></>
                  )
                }
              </Grid>
            </Box>
          </Box>
        </Box>
      </Modal>

      <ModalVideo
        channel='youtube'
        autoplay
        isOpen={isVideoOpen}
        videoId={SHRED_GEOFENCE_VIDEO_ID}
        onClose={() => setIsVideoOpen(false)}
      />
    </>
  );
};

export default LinkGeofenceModal;
